import React, { useState, useEffect } from 'react';
import { Users, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { Twitter, Facebook, Mail } from 'lucide-react';
import { getUsernameFromProfileId } from '../../../../services/firestoreService';
import useBlockStyles from '../common/useBlockStyles';

/**
 * ReferralsRenderer - Renders the referrals block
 * Themed referral CTA with share buttons
 */
const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const ReferralsRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    heading = 'Join ChessWeb',
    description = 'Create your own chess website for free!',
    buttonText = 'Sign Up',
    showShareButtons = true,
  } = content;

  const [username, setUsername] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    const loadUsername = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const fetchedUsername = await getUsernameFromProfileId(profileId);
        setUsername(fetchedUsername);
      } catch (error) {
        console.error('Error loading username:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsername();
  }, [profileId]);

  const referralLink = username ? `https://chessweb.site/signup?ref=${username}` : 'https://chessweb.site/signup';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareText = `${description} Join ChessWeb to create your own chess website!`;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralLink)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
    email: `mailto:?subject=${encodeURIComponent('Create Your Chess Website')}&body=${encodeURIComponent(`${shareText}\n\n${referralLink}`)}`,
  };

  if (loading) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-3 border-gray-600 rounded-full" style={{ borderTopColor: styles.accentColor }}></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-lg mx-auto">
        <div
          className="rounded-2xl p-8 text-center relative overflow-hidden"
          style={{
            backgroundColor: styles.cardBackground,
            border: `1px solid ${styles.accentColor}20`,
          }}
        >
          {/* Subtle accent glow behind icon */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full blur-[80px] opacity-20 pointer-events-none"
            style={{ backgroundColor: styles.accentColor }}
          />

          {/* Icon */}
          <div className="relative">
            <div
              className="w-14 h-14 mx-auto mb-5 rounded-xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${styles.accentColor}, ${styles.accentColor}99)`,
              }}
            >
              <Users size={26} style={{ color: '#ffffff' }} />
            </div>
          </div>

          {/* Heading */}
          {heading && (
            <h2
              className={`${styles.headingSizeClass} font-bold mb-2`}
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
          )}

          {/* Description */}
          {description && (
            <p className="whitespace-pre-wrap mb-6 max-w-sm mx-auto text-sm leading-relaxed" style={{ color: styles.mutedColor, fontFamily: styles.bodyFontStyle }}>
              {description}
            </p>
          )}

          {/* CTA Button */}
          <a
            href={referralLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all hover:opacity-90 hover:translate-y-[-1px]"
            style={styles.getButtonStyles()}
          >
            {buttonText}
            <ExternalLink size={16} />
          </a>

          {/* Divider */}
          <div className="my-6 mx-auto max-w-xs" style={{ borderTop: `1px solid ${styles.accentColor}15` }} />

          {/* Referral Link Copy */}
          <div className="max-w-sm mx-auto">
            <div
              className="flex items-center rounded-lg overflow-hidden"
              style={{
                backgroundColor: 'rgba(0,0,0,0.15)',
                border: `1px solid ${styles.accentColor}15`,
              }}
            >
              <input
                type="text"
                value={referralLink}
                readOnly
                className="flex-1 bg-transparent text-xs px-3 py-2.5 outline-none min-w-0 truncate"
                style={{ color: styles.mutedColor }}
              />
              <button
                onClick={copyLink}
                className="px-4 py-2.5 flex items-center gap-1.5 text-xs font-medium transition-all hover:opacity-80 whitespace-nowrap flex-shrink-0"
                style={{
                  backgroundColor: copied ? `${styles.accentColor}` : `${styles.accentColor}20`,
                  color: copied ? '#ffffff' : styles.accentColor,
                }}
              >
                {copied ? (
                  <>
                    <CheckCircle size={13} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={13} />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Share Buttons */}
          {showShareButtons && (
            <div className="mt-5 flex items-center justify-center gap-2">
              <span className="text-xs mr-1" style={{ color: `${styles.mutedColor}99` }}>
                Share
              </span>
              <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:opacity-80"
                style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: styles.mutedColor }}
                title="Share on Twitter"
              >
                <Twitter size={14} />
              </a>
              <a
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:opacity-80"
                style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: styles.mutedColor }}
                title="Share on Facebook"
              >
                <Facebook size={14} />
              </a>
              <a
                href={shareLinks.email}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:opacity-80"
                style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: styles.mutedColor }}
                title="Share via Email"
              >
                <Mail size={14} />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralsRenderer;
